import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard } from "../components/Brutal";
import DeleteCascadeDialog from "../components/DeleteCascadeDialog";
import { ArrowCounterClockwise, Trash, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TrashPage() {
  const { t } = useTranslation();
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgeTarget, setPurgeTarget] = useState(null); // {product, impact}

  const reload = async () => {
    try {
      const { data } = await api.get("/products/archived");
      setArchived(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/products/archived")
      .then(({ data }) => {
        if (cancelled) return;
        setArchived(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(formatApiError(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRestore = async (p) => {
    try {
      await api.post(`/products/${p.id}/restore`);
      setArchived((rows) => rows.filter((r) => r.id !== p.id));
      toast.success(t("trash.restored", { name: p.name }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handlePurge = async (p, cascade = false) => {
    try {
      await api.post(`/products/${p.id}/purge${cascade ? "?cascade=true" : ""}`);
      setArchived((rows) => rows.filter((r) => r.id !== p.id));
      toast.success(t("trash.purged", { name: p.name }));
      setPurgeTarget(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && detail.requires_cascade) {
        setPurgeTarget({ product: p, impact: detail });
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="trash-page">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-zinc-900 text-white flex items-center justify-center">
          <Trash size={22} weight="bold" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("trash.title")}</h1>
          <p className="text-sm text-zinc-600">{t("trash.subtitle")}</p>
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-zinc-500" data-testid="trash-loading">{t("common.loading")}</p>
      ) : archived.length === 0 ? (
        <BrutalCard>
          <div className="flex items-center gap-3" data-testid="trash-empty">
            <Package size={24} weight="bold" className="text-zinc-400" />
            <p className="font-medium text-zinc-700">{t("trash.empty")}</p>
          </div>
        </BrutalCard>
      ) : (
        <div className="space-y-3" data-testid="trash-list">
          {archived.map((p) => (
            <TrashRow
              key={p.id}
              product={p}
              onRestore={() => handleRestore(p)}
              onPurge={() => handlePurge(p, false)}
            />
          ))}
        </div>
      )}

      {purgeTarget && (
        <DeleteCascadeDialog
          target={purgeTarget}
          onCancel={() => setPurgeTarget(null)}
          onConfirm={() => handlePurge(purgeTarget.product, true)}
        />
      )}
    </div>
  );
}

function TrashRow({ product, onRestore, onPurge }) {
  const { t } = useTranslation();
  const days = Number.isFinite(product.days_remaining) ? product.days_remaining : 30;
  // Critical pill: ≤ 3 days; warning: ≤ 7; otherwise zinc.
  const tone =
    days <= 3
      ? "bg-red-50 text-red-700 border-red-200"
      : days <= 7
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-zinc-50 text-zinc-700 border-zinc-200";
  return (
    <div
      data-testid={`trash-row-${product.id}`}
      className="bg-white border border-zinc-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3"
    >
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-zinc-900 truncate">{product.name}</h3>
        <p className="text-xs text-zinc-500 truncate">
          {t("trash.archived_at", { date: new Date(product.archived_at).toLocaleString() })}
        </p>
      </div>
      <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border tabular-nums ${tone}`} data-testid={`trash-days-${product.id}`}>
        {t("trash.days_remaining", { count: days })}
      </span>
      <div className="flex gap-2 shrink-0">
        <BrutalButton size="sm" variant="secondary" onClick={onRestore} data-testid={`restore-${product.id}`}>
          <ArrowCounterClockwise size={14} weight="bold" /> {t("trash.restore")}
        </BrutalButton>
        <BrutalButton size="sm" variant="warning" onClick={onPurge} data-testid={`purge-${product.id}`}>
          <Trash size={14} weight="bold" /> {t("trash.purge")}
        </BrutalButton>
      </div>
    </div>
  );
}
